import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../components/AppIcon';
import ImportFacturaWizard from './components/ImportFacturaWizard';
import BandejaEmail from './components/BandejaEmail';
import ProductosVincular from './components/ProductosVincular';

import {
  comprasService,
  lineasCompraService,
  proveedoresService,
  productosService,
  almacenesService,
  lotesService,
  movimientosStockService,
  pagosFacturasService,
} from '../../services/tiendaService';
import { tiendaDb } from '../../lib/supabase';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// ─── VER DOCUMENTO ORIGINAL ───────────────────────────────────────────────────
const VerDocumentoOriginal = ({ factura, mode = 'view' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const storagePath = factura?.storage_path;
  const bucket = factura?.storage_bucket;
  const filename = factura?.filename_original || factura?.numero_factura || 'documento';

  // Si no hay bucket o path, mostrar mensaje en lugar de los botones
  if (!bucket || !storagePath) {
    return (
      <span className="font-oranienbaum text-white/30 text-[10px] italic">
        Documento original no disponible
      </span>
    );
  }

  const getSignedUrl = async () => {
    const result = await supabase.storage.from(bucket).createSignedUrl(storagePath, 300);
    if (!result) {
      throw new Error('No se pudo obtener la URL del documento');
    }
    const { data, error: signedErr } = result;
    if (signedErr) {
      console.error('SIGNED URL ERROR', signedErr);
      throw new Error(signedErr.message || 'Error al generar URL firmada');
    }
    if (!data?.signedUrl) {
      throw new Error('No se pudo obtener el documento original');
    }
    return data.signedUrl;
  };

  const handleClick = async () => {
    setError(null);
    setLoading(true);
    try {
      const signedUrl = await getSignedUrl();

      if (mode === 'download') {
        try {
          // Intentar descarga via fetch + Blob para forzar filename
          const response = await fetch(signedUrl);
          if (!response.ok) throw new Error('fetch_failed');
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
        } catch {
          // Fallback: abrir en nueva pestaña si fetch falla
          window.open(signedUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        window.open(signedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      setError(e?.message || 'Error al acceder al documento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={handleClick}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum disabled:opacity-50 transition-all"
        style={{ background: 'rgba(168,85,247,0.15)', color: '#A855F7', border: '1px solid rgba(168,85,247,0.30)' }}
        title={mode === 'download' ? 'Descargar documento original' : 'Ver documento original'}
      >
        {loading ? (
          <div className="w-3 h-3 rounded-full border-2 border-white/20 border-t-purple-400 animate-spin" />
        ) : (
          <Icon name={mode === 'download' ? 'Download' : 'FileText'} size={12} />
        )}
        {mode === 'download' ? 'Descargar documento' : 'Ver documento original'}
      </button>
      {error && <p className="font-oranienbaum text-red-400 text-[10px]">{error}</p>}
    </div>
  );
};

// ─── ASIGNAR PROVEEDOR MODAL ──────────────────────────────────────────────────
const AsignarProveedorModal = ({ factura, proveedores, onClose, onSaved }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // OCR data from factura
  const ocrJson = factura?.ocr_json || {};
  const ocrEmisor = ocrJson?.emisor || ocrJson?.proveedor || {};
  const ocrRazonSocial = ocrEmisor?.razon_social || null;
  const ocrCif = ocrEmisor?.nif_cif || factura?.cif_proveedor || null;
  const ocrEmail = ocrEmisor?.email || null;

  const filtered = proveedores?.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      p?.nombre?.toLowerCase().includes(q) ||
      p?.nombre_comercial?.toLowerCase().includes(q) ||
      p?.nif_cif?.toLowerCase().includes(q) ||
      p?.email?.toLowerCase().includes(q)
    );
  });

  const handleSave = async () => {
    if (!selected) return setError('Selecciona un proveedor de la lista.');
    setSaving(true);
    setError(null);
    try {
      await comprasService?.update(factura?.id, { proveedor_id: selected?.id });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.message || 'Error al asignar el proveedor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.90)' }}>
      <div className="w-full max-w-lg rounded-2xl flex flex-col max-h-[85vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.30)' }}>
              <Icon name="Truck" size={15} style={{ color: '#FBBF24' }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">Asignar proveedor</h3>
              <p className="font-oranienbaum text-white/40 text-xs">{factura?.numero_factura}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* OCR detected data */}
          {(ocrRazonSocial || ocrCif || ocrEmail) && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.20)' }}>
              <p className="font-oranienbaum text-blue-300/70 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Icon name="ScanLine" size={10} />Proveedor detectado por OCR
              </p>
              <div className="space-y-1">
                {ocrRazonSocial && (
                  <div className="flex items-center gap-2">
                    <span className="font-oranienbaum text-white/35 text-xs w-20 flex-shrink-0">Razón social</span>
                    <span className="font-oranienbaum text-white/75 text-xs font-semibold">{ocrRazonSocial}</span>
                  </div>
                )}
                {ocrCif && (
                  <div className="flex items-center gap-2">
                    <span className="font-oranienbaum text-white/35 text-xs w-20 flex-shrink-0">CIF/NIF</span>
                    <span className="font-oranienbaum text-white/75 text-xs font-mono">{ocrCif}</span>
                  </div>
                )}
                {ocrEmail && (
                  <div className="flex items-center gap-2">
                    <span className="font-oranienbaum text-white/35 text-xs w-20 flex-shrink-0">Email</span>
                    <span className="font-oranienbaum text-white/75 text-xs">{ocrEmail}</span>
                  </div>
                )}
              </div>
              <p className="font-oranienbaum text-blue-300/50 text-[10px] mt-2">
                Usa estos datos para buscar el proveedor correcto en la lista.
              </p>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              placeholder="Buscar por nombre, CIF, email..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              autoFocus
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
            />
          </div>

          {/* Supplier list */}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {filtered?.length === 0 ? (
              <p className="font-oranienbaum text-white/30 text-xs text-center py-4">No se encontraron proveedores</p>
            ) : filtered?.map(p => (
              <button
                key={p?.id}
                onClick={() => setSelected(p)}
                className="w-full text-left px-3 py-2.5 rounded-xl transition-all"
                style={selected?.id === p?.id
                  ? { background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)' }
                  : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-oranienbaum text-white/85 text-sm font-semibold truncate">{p?.nombre}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {p?.nif_cif && <span className="font-oranienbaum text-white/40 text-[10px] font-mono">{p?.nif_cif}</span>}
                      {p?.email && <span className="font-oranienbaum text-white/35 text-[10px]">{p?.email}</span>}
                    </div>
                  </div>
                  {selected?.id === p?.id && (
                    <Icon name="CheckCircle2" size={16} style={{ color: '#FBBF24', flexShrink: 0 }} />
                  )}
                </div>
              </button>
            ))}
          </div>

          {error && (
            <div className="p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <Icon name="AlertCircle" size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving || !selected}
            className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
            style={{ background: 'rgba(251,191,36,0.20)', border: '1px solid rgba(251,191,36,0.40)', color: '#FBBF24' }}>
            {saving
              ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-amber-400 animate-spin" />Guardando...</>
              : <><Icon name="Check" size={14} />Asignar proveedor</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const RED = '#F87171';
const AMBER = '#FBBF24';
const ORANGE = '#FB923C';
const PURPLE = '#A78BFA';

const fmt = (n, d = 2) =>
  typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => (typeof n === 'number' ? `${fmt(n)} €` : '—');
const fmtDate = (d) => (d ? new Date(d + 'T00:00:00')?.toLocaleDateString('es-ES') : '—');
const today = () => new Date()?.toISOString()?.split('T')?.[0];

// ─── CALCULAR ESTADO DE PAGO DINÁMICO ────────────────────────────────────────
const calcEstadoPago = (factura) => {
  const estadoPago = factura?.estado_pago || 'PENDIENTE';
  const pendiente = parseFloat(factura?.pendiente_pago ?? factura?.total ?? 0);
  const fechaVenc = factura?.fecha_vencimiento;

  if (estadoPago === 'PAGADA') return { estado: 'PAGADA', label: 'Pagada', color: '#4ADE80', dias: null };
  if (estadoPago === 'ANULADA') return { estado: 'ANULADA', label: 'Anulada', color: 'rgba(255,255,255,0.30)', dias: null };

  if (!fechaVenc) return { estado: 'SIN_VENCIMIENTO', label: 'Sin vencimiento', color: 'rgba(255,255,255,0.30)', dias: null };

  const hoy = new Date();
  hoy?.setHours(0, 0, 0, 0);
  const venc = new Date(fechaVenc + 'T00:00:00');
  const dias = Math.round((venc - hoy) / (1000 * 60 * 60 * 24));

  if (pendiente <= 0) return { estado: 'PAGADA', label: 'Pagada', color: '#4ADE80', dias: null };
  if (dias < 0) return { estado: 'VENCIDA', label: 'Vencida', color: RED, dias: Math.abs(dias) };
  if (dias === 0) return { estado: 'HOY', label: 'Vence hoy', color: AMBER, dias: 0 };
  if (dias <= 7) return { estado: 'URGENTE', label: 'Urgente', color: ORANGE, dias };
  if (estadoPago === 'PARCIAL') return { estado: 'PARCIAL', label: 'Parcial', color: PURPLE, dias };
  return { estado: 'PENDIENTE', label: 'Pendiente', color: AMBER, dias };
};

// ─── VENCIMIENTO BADGE ────────────────────────────────────────────────────────
const VencimientoBadge = ({ factura }) => {
  const info = calcEstadoPago(factura);
  if (info?.estado === 'PAGADA') return <span className="font-oranienbaum text-xs" style={{ color: '#4ADE80' }}>✓ Pagada</span>;
  if (info?.estado === 'ANULADA') return <span className="font-oranienbaum text-xs text-white/25">Anulada</span>;
  if (info?.estado === 'SIN_VENCIMIENTO') return <span className="font-oranienbaum text-xs text-white/25">Sin definir</span>;

  const fecha = factura?.fecha_vencimiento ? fmtDate(factura?.fecha_vencimiento) : '—';

  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-oranienbaum text-xs text-white/60">{fecha}</span>
      {info?.estado === 'VENCIDA' && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
          style={{ background: `${RED}15`, color: RED, border: `1px solid ${RED}30` }}>
          <Icon name="AlertCircle" size={8} />{info?.dias}d retraso
        </span>
      )}
      {info?.estado === 'HOY' && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
          style={{ background: `${AMBER}15`, color: AMBER, border: `1px solid ${AMBER}30` }}>
          <Icon name="Clock" size={8} />Hoy
        </span>
      )}
      {info?.estado === 'URGENTE' && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
          style={{ background: `${ORANGE}15`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
          <Icon name="Clock" size={8} />{info?.dias}d
        </span>
      )}
      {(info?.estado === 'PENDIENTE' || info?.estado === 'PARCIAL') && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px]"
          style={{ background: `${BLUE}08`, color: BLUE, border: `1px solid ${BLUE}15` }}>
          {info?.dias}d
        </span>
      )}
    </div>
  );
};

// ─── ESTADO PAGO BADGE ───────────────────────────────────────────────────────
const EstadoPagoBadge = ({ factura }) => {
  const info = calcEstadoPago(factura);
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum whitespace-nowrap"
      style={{ background: `${info?.color}18`, color: info?.color, border: `1px solid ${info?.color}30` }}>
      {info?.label}
    </span>
  );
};

const ESTADO_OPTS = ['Todas', 'PENDIENTE', 'RECEPCIONADA', 'PAGADA', 'VENCIDA'];

const emptyLine = () => ({
  _key: Math.random()?.toString(36)?.slice(2),
  producto_id: '',
  descripcion: '',
  cantidad: '',
  precio_unitario: '',
  subtotal: 0,
});

// ─── MODAL REGISTRAR PAGO ────────────────────────────────────────────────────
const PagoModal = ({ factura, onClose, onSaved }) => {
  const { user } = useAuth();
  const [tipo, setTipo] = useState('total'); // 'total' | 'parcial'
  const [importe, setImporte] = useState('');
  const [fechaPago, setFechaPago] = useState(today());
  const [formaPago, setFormaPago] = useState('TRANSFERENCIA');
  const [referencia, setReferencia] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pendiente = parseFloat(factura?.pendiente_pago ?? factura?.total ?? 0);
  const total = parseFloat(factura?.total ?? 0);

  const handleSave = async () => {
    setError(null);
    if (tipo === 'parcial' && (!importe || parseFloat(importe) <= 0)) {
      setError('Introduce un importe válido mayor que 0.');
      return;
    }
    if (tipo === 'parcial' && parseFloat(importe) > pendiente) {
      setError(`El importe no puede superar el pendiente (${fmtEur(pendiente)}).`);
      return;
    }
    try {
      setSaving(true);
      await pagosFacturasService?.registrarPago({
        facturaId: factura?.id,
        importe: tipo === 'total' ? pendiente : parseFloat(importe),
        fechaPago,
        formaPago,
        referencia,
        observaciones,
        createdBy: user?.email || 'ERP',
        esTotal: tipo === 'total',
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.message || 'Error al registrar el pago');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none';
  const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
  const selectSt = { background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl flex flex-col" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="CreditCard" size={15} style={{ color: ACCENT }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">Registrar Pago</h3>
              <p className="font-oranienbaum text-white/40 text-xs">{factura?.numero_factura} · {factura?.proveedor?.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* Resumen */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="font-oranienbaum text-white/35 text-[10px]">Total</p>
              <p className="font-oranienbaum text-white font-bold text-sm">{fmtEur(total)}</p>
            </div>
            <div className="p-3 rounded-xl text-center" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}20` }}>
              <p className="font-oranienbaum text-white/35 text-[10px]">Pagado</p>
              <p className="font-oranienbaum font-bold text-sm" style={{ color: ACCENT }}>{fmtEur(parseFloat(factura?.importe_pagado) || 0)}</p>
            </div>
            <div className="p-3 rounded-xl text-center" style={{ background: `${AMBER}08`, border: `1px solid ${AMBER}20` }}>
              <p className="font-oranienbaum text-white/35 text-[10px]">Pendiente</p>
              <p className="font-oranienbaum font-bold text-sm" style={{ color: AMBER }}>{fmtEur(pendiente)}</p>
            </div>
          </div>

          {/* Tipo de pago */}
          <div className="flex gap-2">
            <button onClick={() => setTipo('total')}
              className="flex-1 py-2 rounded-xl text-xs font-oranienbaum font-semibold transition-all"
              style={tipo === 'total' ? { background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT } : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
              Pago completo
            </button>
            <button onClick={() => setTipo('parcial')}
              className="flex-1 py-2 rounded-xl text-xs font-oranienbaum font-semibold transition-all"
              style={tipo === 'parcial' ? { background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE } : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
              Pago parcial
            </button>
          </div>

          {tipo === 'parcial' && (
            <div>
              <label className="block font-oranienbaum text-white/45 text-xs mb-1">Importe a pagar *</label>
              <input type="number" min="0.01" step="0.01" max={pendiente} value={importe}
                onChange={e => setImporte(e?.target?.value)}
                className={inputCls} style={inputSt} placeholder={`Máx. ${fmtEur(pendiente)}`} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-oranienbaum text-white/45 text-xs mb-1">Fecha de pago</label>
              <input type="date" value={fechaPago} onChange={e => setFechaPago(e?.target?.value)}
                className={inputCls} style={{ ...inputSt, colorScheme: 'dark' }} />
            </div>
            <div>
              <label className="block font-oranienbaum text-white/45 text-xs mb-1">Forma de pago</label>
              <select value={formaPago} onChange={e => setFormaPago(e?.target?.value)} className={inputCls} style={selectSt}>
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="EFECTIVO">Efectivo</option>
                <option value="TARJETA">Tarjeta</option>
                <option value="CHEQUE">Cheque</option>
                <option value="PAGARÉ">Pagaré</option>
                <option value="CONFIRMING">Confirming</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block font-oranienbaum text-white/45 text-xs mb-1">Referencia</label>
            <input type="text" value={referencia} onChange={e => setReferencia(e?.target?.value)}
              className={inputCls} style={inputSt} placeholder="Nº transferencia, cheque..." />
          </div>
          <div>
            <label className="block font-oranienbaum text-white/45 text-xs mb-1">Observaciones</label>
            <textarea value={observaciones} onChange={e => setObservaciones(e?.target?.value)}
              rows={2} className={inputCls} style={{ ...inputSt, resize: 'none' }} />
          </div>

          {error && (
            <div className="p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <Icon name="AlertCircle" size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
            {saving ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#34D399] animate-spin" />Guardando...</> : <><Icon name="Check" size={14} />Registrar Pago</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL EDITAR VENCIMIENTO ─────────────────────────────────────────────────
const EditVencimientoModal = ({ factura, onClose, onSaved }) => {
  const [fecha, setFecha] = useState(factura?.fecha_vencimiento || '');
  const [formaPago, setFormaPago] = useState(factura?.forma_pago || '');
  const [observaciones, setObservaciones] = useState(factura?.observaciones_pago || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await pagosFacturasService?.actualizarVencimiento(
        factura?.id,
        fecha || null,
        formaPago || null,
        observaciones || null
      );
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.message || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none';
  const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
  const selectSt = { background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30` }}>
              <Icon name="Calendar" size={15} style={{ color: BLUE }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">Editar Vencimiento</h3>
              <p className="font-oranienbaum text-white/40 text-xs">{factura?.numero_factura}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block font-oranienbaum text-white/45 text-xs mb-1">Fecha de vencimiento</label>
            <input type="date" value={fecha} onChange={e => setFecha(e?.target?.value)}
              className={inputCls} style={{ ...inputSt, colorScheme: 'dark' }} />
            <p className="font-oranienbaum text-white/25 text-[10px] mt-1">Deja vacío para "Sin vencimiento definido"</p>
          </div>
          <div>
            <label className="block font-oranienbaum text-white/45 text-xs mb-1">Forma de pago</label>
            <select value={formaPago} onChange={e => setFormaPago(e?.target?.value)} className={inputCls} style={selectSt}>
              <option value="">— Sin especificar —</option>
              <option value="TRANSFERENCIA">Transferencia</option>
              <option value="EFECTIVO">Efectivo</option>
              <option value="TARJETA">Tarjeta</option>
              <option value="CHEQUE">Cheque</option>
              <option value="PAGARÉ">Pagaré</option>
              <option value="CONFIRMING">Confirming</option>
            </select>
          </div>
          <div>
            <label className="block font-oranienbaum text-white/45 text-xs mb-1">Observaciones</label>
            <textarea value={observaciones} onChange={e => setObservaciones(e?.target?.value)}
              rows={2} className={inputCls} style={{ ...inputSt, resize: 'none' }} placeholder="Notas sobre el pago..." />
          </div>
          {fecha && (
            <div className="p-2 rounded-lg" style={{ background: `${BLUE}08`, border: `1px solid ${BLUE}15` }}>
              {(() => {
                const hoy = new Date(); hoy?.setHours(0,0,0,0);
                const venc = new Date(fecha + 'T00:00:00');
                const dias = Math.round((venc - hoy) / (1000 * 60 * 60 * 24));
                if (dias < 0) return <p className="font-oranienbaum text-xs" style={{ color: RED }}>⚠ Vencida hace {Math.abs(dias)} días</p>;
                if (dias === 0) return <p className="font-oranienbaum text-xs" style={{ color: AMBER }}>⚠ Vence hoy</p>;
                return <p className="font-oranienbaum text-xs" style={{ color: BLUE }}>Vence en {dias} días</p>;
              })()}
            </div>
          )}
          {error && <p className="font-oranienbaum text-red-400 text-xs">{error}</p>}
        </div>
        <div className="flex gap-3 px-5 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-50"
            style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── COMPLETAR IMPORTACIÓN MODAL ──────────────────────────────────────────────
const CompletarImportacionModal = ({ factura, productos, onClose, onSaved }) => {
  const { user } = useAuth();
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [almacenes, setAlmacenes] = useState([]);
  const [almacenId, setAlmacenId] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const full = await comprasService?.getById(factura?.id);
        const ls = (full?.lineas || [])?.map(l => ({
          ...l,
          _afecta_stock: l?.afecta_stock !== false,
          _producto_asignado: l?.producto_id ? productos?.find(p => p?.id === l?.producto_id) : null,
        }));
        setLineas(ls);
        const { data: alms } = await tiendaDb?.from('almacenes')?.select('id, nombre')?.eq('activo', true);
        setAlmacenes(alms || []);
        if (alms?.length > 0) setAlmacenId(alms?.[0]?.id);
      } catch (e) {
        setError(e?.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [factura?.id]);

  const handleCompletar = async () => {
    setSaving(true);
    setError(null);

    try {
      // Use the transactional confirmar service (idempotent)
      const { confirmarFacturaYEntradaMercancia } = await import('../../services/purchaseInvoiceService');
      const result = await confirmarFacturaYEntradaMercancia(factura?.id, {
        usuario: user?.email || 'ERP',
      });

      if (!result?.ok) {
        throw new Error(result?.error || 'Error al confirmar la factura');
      }

      setResultado({
        productosCreados: 0,
        productosVinculados: lineas?.filter(l => l?.producto_id)?.length,
        movimientosCreados: result?.lineasStock || 0,
        lotesCreados: result?.lotesCreados || 0,
        lineasExcluidas: lineas?.filter(l => l?.afecta_stock === false)?.length,
        pvpActualizados: result?.pvpActualizados || 0,
        idempotent: result?.idempotent || false,
        errores: result?.errores || [],
      });
      onSaved?.();
    } catch (e) {
      setError(e?.message || 'Error al completar la importación');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-2xl rounded-2xl flex flex-col max-h-[90vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="PackageCheck" size={15} style={{ color: GOLD }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">Confirmar factura y entrada de mercancía</h3>
              <p className="font-oranienbaum text-white/40 text-xs">{factura?.numero_factura} · {factura?.proveedor?.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" /></div>
          ) : resultado ? (
            <div className="space-y-4">
              <div className="p-4 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}20` }}>
                <p className="font-oranienbaum text-white font-semibold mb-3">
                  {resultado?.idempotent ? '✓ Factura ya recepcionada (sin cambios)' : '✓ Factura confirmada y mercancía recepcionada'}
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs font-oranienbaum">
                  <div className="flex justify-between"><span className="text-white/50">Movimientos ENTRADA:</span><span className="text-white font-bold">{resultado?.movimientosCreados}</span></div>
                  <div className="flex justify-between"><span className="text-white/50">Lotes creados:</span><span className="text-white font-bold">{resultado?.lotesCreados}</span></div>
                  <div className="flex justify-between"><span className="text-white/50">PVP actualizados:</span><span className="text-white font-bold">{resultado?.pvpActualizados}</span></div>
                  <div className="flex justify-between"><span className="text-white/50">Líneas excluidas:</span><span className="text-white font-bold">{resultado?.lineasExcluidas}</span></div>
                </div>
                {resultado?.errores?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {resultado?.errores?.map((e, i) => <p key={i} className="font-oranienbaum text-amber-400 text-xs">{e}</p>)}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="p-3 rounded-xl" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}20` }}>
                <p className="font-oranienbaum text-amber-300/80 text-xs font-semibold mb-1">Esta acción realizará en una sola operación:</p>
                <ul className="font-oranienbaum text-white/50 text-xs space-y-0.5 list-disc list-inside">
                  <li>Crear lotes para productos que los requieran</li>
                  <li>Crear movimientos ENTRADA en el stock</li>
                  <li>Actualizar inventario_actual con coste medio ponderado</li>
                  <li>Actualizar ultimo_coste y coste_medio del producto</li>
                  <li>Guardar historico_precios_compra con IVA y recargo</li>
                  <li>Calcular y actualizar PVP sugerido (respetando bloqueo_precio)</li>
                  <li>Registrar pago pendiente</li>
                </ul>
                <p className="font-oranienbaum text-white/40 text-[10px] mt-2">✓ Idempotente: seguro de ejecutar varias veces sin duplicar stock</p>
              </div>

              <div className="space-y-2">
                <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">Líneas de la factura ({lineas?.length})</p>
                {lineas?.map((linea, idx) => (
                  <div key={linea?.id || idx} className="rounded-xl p-3 flex items-center gap-3"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex-1 min-w-0">
                      <p className="font-oranienbaum text-white/80 text-xs font-semibold truncate">{linea?.descripcion_original || linea?.descripcion || '—'}</p>
                      <p className="font-oranienbaum text-white/40 text-[10px]">
                        {linea?.cantidad} {linea?.unidad || 'uds'} · {fmtEur(linea?.precio_compra_neto || linea?.precio_unitario)}
                        {linea?.numero_lote && <span className="ml-2 text-purple-400">Lote: {linea?.numero_lote}</span>}
                        {linea?.producto_id
                          ? <span className="ml-2 text-green-400">✓ Vinculado</span>
                          : <span className="ml-2 text-amber-400">⚠ Sin vincular (se omitirá del stock)</span>}
                        {linea?.afecta_stock === false && <span className="ml-2 text-white/30">— excluido</span>}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {linea?.pvp_calculado > 0 && (
                        <p className="font-oranienbaum text-[10px]" style={{ color: BLUE }}>PVP: {fmtEur(linea?.pvp_calculado)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {error && (
                <div className="p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                  <Icon name="AlertCircle" size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            {resultado ? 'Cerrar' : 'Cancelar'}
          </button>
          {!resultado && (
            <>
              {lineas?.length === 0 ? (
                <div className="flex-1 flex flex-col gap-1">
                  <button disabled
                    className="w-full py-2.5 rounded-xl text-sm font-oranienbaum font-semibold opacity-40 cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
                    <Icon name="PackageCheck" size={14} />Confirmar factura y entrada de mercancía
                  </button>
                  <p className="font-oranienbaum text-amber-400/80 text-[10px] text-center">
                    No se puede confirmar la entrada porque la factura no contiene líneas importadas.
                  </p>
                </div>
              ) : (
                <button onClick={handleCompletar} disabled={saving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }}>
                  {saving ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#D6EF4E] animate-spin" />Confirmando...</> : <><Icon name="PackageCheck" size={14} />Confirmar factura y entrada de mercancía</>}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TiendaCompras = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('facturas'); // 'facturas' | 'bandeja_email' | 'vincular'
  const [facturas, setFacturas] = useState([]);
  const [kpis, setKpis] = useState({});
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('Todas');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editFactura, setEditFactura] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [detailFactura, setDetailFactura] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [pagoFactura, setPagoFactura] = useState(null);
  const [vencimientoFactura, setVencimientoFactura] = useState(null);
  const [completarFactura, setCompletarFactura] = useState(null);
  const [almacenes, setAlmacenes] = useState([]);
  const [recepcionFactura, setRecepcionFactura] = useState(null);
  const [recepcionLoading, setRecepcionLoading] = useState(false);
  const [recepcionError, setRecepcionError] = useState(null);
  const [recepcionSuccess, setRecepcionSuccess] = useState(null);
  const [recepcionAlmacen, setRecepcionAlmacen] = useState('');
  const [recepcionLineas, setRecepcionLineas] = useState([]);
  const [asignarProveedorFactura, setAsignarProveedorFactura] = useState(null);

  const [form, setForm] = useState({ proveedor_id: '', numero_factura: '', fecha_factura: today(), observaciones: '' });
  const [lineas, setLineas] = useState([emptyLine()]);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setListError(null);
      const [data, kpiData, provs, prods] = await Promise.all([
        comprasService?.getAll({ search, estado: estadoFilter }),
        comprasService?.getKPIs(),
        proveedoresService?.getAll(),
        productosService?.getAll({ activo: true }),
      ]);
      setFacturas(data);
      setKpis(kpiData);
      setProveedores(provs);
      setProductos(prods);
      setPage(1);
    } catch (err) {
      setListError(err?.message || 'Error al cargar compras');
    } finally {
      setLoading(false);
    }
  }, [search, estadoFilter]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (showForm || recepcionFactura) {
      almacenesService?.getAll()?.then(setAlmacenes)?.catch(() => {});
    }
  }, [showForm, recepcionFactura]);

  const openNewForm = () => {
    setEditFactura(null);
    setForm({ proveedor_id: '', numero_factura: '', fecha_factura: today(), observaciones: '' });
    setLineas([emptyLine()]);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = async (factura) => {
    setFormError(null);
    setFormSaving(false);
    try {
      const full = await comprasService?.getById(factura?.id);
      setEditFactura(full);
      setForm({ proveedor_id: full?.proveedor_id || '', numero_factura: full?.numero_factura || '', fecha_factura: full?.fecha_factura || today(), observaciones: full?.observaciones || '' });
      const ls = (full?.lineas || [])?.map(l => ({ _key: l?.id, id: l?.id, producto_id: l?.producto_id || '', descripcion: l?.descripcion || '', cantidad: l?.cantidad ?? '', precio_unitario: l?.precio_unitario ?? '', subtotal: l?.subtotal ?? 0 }));
      setLineas(ls?.length ? ls : [emptyLine()]);
      setShowForm(true);
    } catch (err) { setListError(err?.message || 'Error al cargar factura'); }
  };

  const openDetail = async (factura) => {
    setDetailFactura(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const full = await comprasService?.getById(factura?.id);
      setDetailFactura(full);
    } catch (err) {
      setDetailError(err?.message || 'Error al cargar detalle');
      setDetailFactura(factura);
    } finally {
      setDetailLoading(false);
    }
  };

  const openRecepcionar = async (factura) => {
    setRecepcionError(null);
    setRecepcionSuccess(null);
    setRecepcionAlmacen('');
    try {
      const full = await comprasService?.getById(factura?.id);
      const ls = (full?.lineas || [])?.map(l => ({ ...l, numero_lote: '', fecha_caducidad: '', _producto: l?.producto || null }));
      setRecepcionLineas(ls);
      setRecepcionFactura(full);
    } catch (err) { setListError(err?.message || 'Error al cargar factura para recepción'); }
  };

  const updateLinea = (key, field, value) => {
    setLineas(prev => prev?.map(l => {
      if (l?._key !== key) return l;
      const updated = { ...l, [field]: value };
      if (field === 'cantidad' || field === 'precio_unitario') {
        const qty = parseFloat(field === 'cantidad' ? value : updated?.cantidad) || 0;
        const price = parseFloat(field === 'precio_unitario' ? value : updated?.precio_unitario) || 0;
        updated.subtotal = qty * price;
      }
      if (field === 'producto_id') {
        const prod = productos?.find(p => p?.id === value);
        if (prod && !updated?.descripcion) updated.descripcion = prod?.nombre;
      }
      return updated;
    }));
  };

  const addLinea = () => setLineas(prev => [...prev, emptyLine()]);
  const removeLinea = (key) => setLineas(prev => prev?.filter(l => l?._key !== key));

  const baseImponible = lineas?.reduce((s, l) => s + (parseFloat(l?.subtotal) || 0), 0);
  const ivaEstimado = baseImponible * 0.1;
  const totalEstimado = baseImponible + ivaEstimado;

  const saveFactura = async () => {
    setFormError(null);
    if (!form?.proveedor_id) return setFormError('Selecciona un proveedor.');
    if (!form?.numero_factura?.trim()) return setFormError('El número de factura es obligatorio.');
    if (!form?.fecha_factura) return setFormError('La fecha es obligatoria.');
    const validLineas = lineas?.filter(l => l?.cantidad && l?.precio_unitario);
    if (validLineas?.length === 0) return setFormError('Añade al menos una línea con cantidad y precio.');

    setFormSaving(true);
    try {
      const cabecera = {
        proveedor_id: form?.proveedor_id,
        numero_factura: form?.numero_factura?.trim(),
        fecha_factura: form?.fecha_factura,
        observaciones: form?.observaciones || null,
        base_imponible: baseImponible,
        iva: ivaEstimado,
        total: totalEstimado,
        estado: editFactura?.estado || 'PENDIENTE',
        estado_pago: editFactura?.estado_pago || 'PENDIENTE',
        importe_pagado: editFactura?.importe_pagado || 0,
        pendiente_pago: totalEstimado,
        ocr_procesado: false,
      };

      let facturaId;
      if (editFactura) {
        await comprasService?.update(editFactura?.id, cabecera);
        facturaId = editFactura?.id;
        await lineasCompraService?.deleteByFactura(facturaId);
      } else {
        const created = await comprasService?.create(cabecera);
        facturaId = created?.id;
      }

      const lineasPayload = validLineas?.map(l => ({
        factura_id: facturaId,
        producto_id: l?.producto_id || null,
        descripcion: l?.descripcion || null,
        cantidad: parseFloat(l?.cantidad),
        precio_unitario: parseFloat(l?.precio_unitario),
        subtotal: parseFloat(l?.subtotal) || 0,
        ocr_estado: 'MANUAL',
      }));
      await lineasCompraService?.insertBulk(lineasPayload);

      setShowForm(false);
      await loadList();
    } catch (err) {
      setFormError(err?.message || 'Error al guardar la factura');
    } finally {
      setFormSaving(false);
    }
  };

  const ejecutarRecepcion = async () => {
    setRecepcionError(null);
    if (!recepcionAlmacen) return setRecepcionError('Selecciona el almacén de destino.');
    if (!recepcionLineas?.length) return setRecepcionError('No hay líneas para recepcionar.');

    setRecepcionLoading(true);
    try {
      for (const linea of recepcionLineas) {
        if (!linea?.producto_id || !linea?.cantidad) continue;
        let loteId = null;
        const prod = productos?.find(p => p?.id === linea?.producto_id) || linea?._producto;
        if (prod?.controla_lotes) {
          const numeroLote = linea?.numero_lote?.trim() || `L-${recepcionFactura?.numero_factura}-${Date.now()}`;
          const lotePayload = { producto_id: linea?.producto_id, numero_lote: numeroLote, fecha_entrada: recepcionFactura?.fecha_factura || today(), fecha_caducidad: linea?.fecha_caducidad || null, cantidad_inicial: parseFloat(linea?.cantidad), cantidad_actual: parseFloat(linea?.cantidad), coste_unitario: linea?.precio_unitario ? parseFloat(linea?.precio_unitario) : null };
          const loteCreado = await lotesService?.create(lotePayload);
          loteId = loteCreado?.id;
        }
        await movimientosStockService?.create({
          producto_id: linea?.producto_id,
          lote_id: loteId,
          tipo_movimiento: 'ENTRADA',
          cantidad: parseFloat(linea?.cantidad),
          coste_unitario: linea?.precio_unitario ? parseFloat(linea?.precio_unitario) : null,
          almacen_destino_id: recepcionAlmacen,
          referencia_tipo: 'FACTURA_COMPRA',
          referencia_id: recepcionFactura?.id,
          observaciones: `Recepción factura ${recepcionFactura?.numero_factura}`,
          usuario: user?.email || 'ERP',
        });
      }
      await comprasService?.update(recepcionFactura?.id, { estado: 'RECEPCIONADA', estado_recepcion: 'RECEPCIONADA' });
      setRecepcionSuccess('Mercancía recepcionada correctamente. Inventario actualizado.');
      await loadList();
    } catch (err) {
      setRecepcionError(err?.message || 'Error durante la recepción');
    } finally {
      setRecepcionLoading(false);
    }
  };

  const paged = facturas?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(facturas?.length / PAGE_SIZE);

  const kpiCards = [
    { label: 'Compras del Mes', value: fmtEur(kpis?.comprasMes), icon: 'ShoppingBag', color: ACCENT },
    { label: 'IVA Soportado', value: fmtEur(kpis?.ivaSoportado), icon: 'Percent', color: GOLD },
    { label: 'Pendiente de Pago', value: fmtEur(kpis?.pendientePago), icon: 'AlertCircle', color: AMBER },
    { label: 'Facturas Vencidas', value: kpis?.facturasVencidas ?? '—', icon: 'AlertTriangle', color: RED },
    { label: 'Vence en 7 días', value: kpis?.vence7dias ?? '—', icon: 'Clock', color: ORANGE },
    { label: 'Facturas Pendientes', value: kpis?.facturasPendientes ?? '—', icon: 'FileText', color: BLUE },
    { label: 'Proveedores Activos', value: kpis?.proveedoresActivos ?? '—', icon: 'Truck', color: '#4ADE80' },
  ];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg,#0A0A0A 0%,#111111 50%,#0D0D0D 100%)' }}>
      {showImportWizard && (
        <ImportFacturaWizard
          proveedores={proveedores}
          productos={productos}
          onClose={() => setShowImportWizard(false)}
          onSaved={() => { setShowImportWizard(false); loadList(); }}
        />
      )}
      {pagoFactura && (
        <PagoModal
          factura={pagoFactura}
          onClose={() => setPagoFactura(null)}
          onSaved={() => { setPagoFactura(null); loadList(); }}
        />
      )}
      {vencimientoFactura && (
        <EditVencimientoModal
          factura={vencimientoFactura}
          onClose={() => setVencimientoFactura(null)}
          onSaved={() => { setVencimientoFactura(null); loadList(); }}
        />
      )}
      {completarFactura && (
        <CompletarImportacionModal
          factura={completarFactura}
          productos={productos}
          onClose={() => setCompletarFactura(null)}
          onSaved={() => { setCompletarFactura(null); loadList(); }}
        />
      )}
      {asignarProveedorFactura && (
        <AsignarProveedorModal
          factura={asignarProveedorFactura}
          proveedores={proveedores}
          onClose={() => setAsignarProveedorFactura(null)}
          onSaved={() => {
            setAsignarProveedorFactura(null);
            loadList();
            // Reload detail if open
            if (detailFactura?.id === asignarProveedorFactura?.id) {
              openDetail(asignarProveedorFactura);
            }
          }}
        />
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30` }}>
              <Icon name="ShoppingBag" size={18} style={{ color: BLUE }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Compras</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Facturas de proveedores · Tienda</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={loadList} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={13} />Actualizar
          </button>
          <button onClick={openNewForm} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.55)' }}>
            <Icon name="PenLine" size={13} /><span className="hidden sm:inline">Nueva factura manual</span><span className="sm:hidden">Manual</span>
          </button>
          <button onClick={() => setShowImportWizard(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-oranienbaum font-bold transition-all shadow-lg"
            style={{ background: `linear-gradient(135deg, ${BLUE}30, ${BLUE}15)`, border: `1.5px solid ${BLUE}60`, color: BLUE, boxShadow: `0 0 20px ${BLUE}20` }}>
            <Icon name="ScanLine" size={16} />📷 IMPORTAR FACTURA
          </button>
        </div>
      </div>
      {/* Tab switcher */}
      <div className="flex items-center gap-1 p-1 rounded-xl mb-5 w-fit" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <button onClick={() => setActiveTab('facturas')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-oranienbaum transition-all"
          style={activeTab === 'facturas'
            ? { background: `${BLUE}20`, color: BLUE, border: `1px solid ${BLUE}40` }
            : { color: 'rgba(255,255,255,0.45)', border: '1px solid transparent' }}>
          <Icon name="FileText" size={14} />Facturas
        </button>
        <button onClick={() => setActiveTab('bandeja_email')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-oranienbaum transition-all"
          style={activeTab === 'bandeja_email'
            ? { background: 'rgba(34,211,238,0.15)', color: '#22D3EE', border: '1px solid rgba(34,211,238,0.40)' }
            : { color: 'rgba(255,255,255,0.45)', border: '1px solid transparent' }}>
          <Icon name="Inbox" size={14} />Bandeja email
        </button>
        <button onClick={() => setActiveTab('vincular')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-oranienbaum transition-all"
          style={activeTab === 'vincular'
            ? { background: 'rgba(214,239,78,0.15)', color: '#D6EF4E', border: '1px solid rgba(214,239,78,0.40)' }
            : { color: 'rgba(255,255,255,0.45)', border: '1px solid transparent' }}>
          <Icon name="Link" size={14} />Productos por vincular
        </button>
      </div>

      {/* Bandeja email tab */}
      {activeTab === 'bandeja_email' && <BandejaEmail />}

      {/* Productos por vincular tab */}
      {activeTab === 'vincular' && <ProductosVincular />}

      {/* Facturas tab content */}
      {activeTab === 'facturas' && <>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-xl p-3 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
              <Icon name={kpi?.icon} size={13} style={{ color: kpi?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white text-lg">{loading ? '...' : kpi?.value}</p>
            <p className="font-oranienbaum text-white/45 text-[10px] leading-tight">{kpi?.label}</p>
          </div>
        ))}
      </div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Buscar por número, proveedor..." value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
        </div>
        <div className="flex items-center gap-1 p-1 rounded-xl flex-wrap" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {ESTADO_OPTS?.map(s => (
            <button key={s} onClick={() => setEstadoFilter(s)}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
              style={estadoFilter === s ? { background: `${BLUE}20`, color: BLUE, border: `1px solid ${BLUE}40` } : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
              {s === 'Todas' ? 'Todas' : s}
            </button>
          ))}
        </div>
      </div>
      {listError && (
        <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{listError}</p>
        </div>
      )}
      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <span className="font-oranienbaum text-white/50 text-xs">{facturas?.length} facturas</span>
          <span className="font-oranienbaum text-white/30 text-xs">OCR: <span style={{ color: ACCENT }}>{facturas?.filter(f => f?.ocr_procesado)?.length}</span></span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Número', 'Fecha', 'Proveedor', 'Base Imp.', 'IVA', 'Total', 'Pendiente', 'Vencimiento', 'Estado Pago', 'OCR', 'Acciones']?.map(col => (
                  <th key={col} className="px-3 py-3 text-left whitespace-nowrap">
                    <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#60A5FA] animate-spin" />
                    <p className="font-oranienbaum text-white/40 text-sm">Cargando compras...</p>
                  </div>
                </td></tr>
              ) : paged?.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-5">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${BLUE}08`, border: `1px solid ${BLUE}15` }}>
                      <Icon name="ScanLine" size={28} style={{ color: `${BLUE}50` }} />
                    </div>
                    <div>
                      <p className="font-oranienbaum text-white/50 text-sm mb-1">No hay facturas de compra</p>
                      <p className="font-oranienbaum text-white/25 text-xs">Importa una factura con la cámara o sube un PDF</p>
                    </div>
                    <button onClick={() => setShowImportWizard(true)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-oranienbaum font-bold"
                      style={{ background: `${BLUE}20`, color: BLUE, border: `1px solid ${BLUE}40` }}>
                      <Icon name="ScanLine" size={15} />📷 Importar primera factura
                    </button>
                  </div>
                </td></tr>
              ) : paged?.map((f, i) => {
                const pendiente = parseFloat(f?.pendiente_pago ?? f?.total ?? 0);
                const estadoInfo = calcEstadoPago(f);
                return (
                  <tr key={f?.id}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                    className="hover:bg-white/[0.03] transition-colors">
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/85 text-sm font-semibold">{f?.numero_factura}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/60 text-sm">{fmtDate(f?.fecha_factura)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/70 text-sm">{f?.proveedor?.nombre || '—'}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/70 text-sm">{fmtEur(f?.base_imponible)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/50 text-sm">{fmtEur(f?.iva)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white font-semibold text-sm">{fmtEur(f?.total)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-sm font-semibold" style={{ color: pendiente > 0 ? AMBER : ACCENT }}>
                        {fmtEur(pendiente)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <VencimientoBadge factura={f} />
                    </td>
                    <td className="px-3 py-3">
                      <EstadoPagoBadge factura={f} />
                    </td>
                    <td className="px-3 py-3">
                      {f?.ocr_procesado ? (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
                            style={{ background: `${ACCENT}15`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>IA</span>
                          {f?.ocr_confianza != null && (
                            <span className="font-oranienbaum text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                              {f?.ocr_confianza > 1 ? Math.round(f?.ocr_confianza) : Math.round(f?.ocr_confianza * 100)}%
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="font-oranienbaum text-white/25 text-xs">Manual</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openDetail(f)} title="Ver detalle"
                          className="p-1.5 rounded-lg transition-colors hover:bg-white/10" style={{ color: 'rgba(255,255,255,0.50)' }}>
                          <Icon name="Eye" size={14} />
                        </button>
                        <button onClick={() => setVencimientoFactura(f)} title="Editar vencimiento"
                          className="p-1.5 rounded-lg transition-colors hover:bg-white/10" style={{ color: BLUE }}>
                          <Icon name="Calendar" size={14} />
                        </button>
                        {estadoInfo?.estado !== 'PAGADA' && estadoInfo?.estado !== 'ANULADA' && (
                          <button onClick={() => setPagoFactura(f)} title="Registrar pago"
                            className="p-1.5 rounded-lg transition-colors hover:bg-white/10" style={{ color: ACCENT }}>
                            <Icon name="CreditCard" size={14} />
                          </button>
                        )}
                        {f?.ocr_procesado && (
                          <button onClick={() => setCompletarFactura(f)} title="Completar importación"
                            className="p-1.5 rounded-lg transition-colors hover:bg-white/10" style={{ color: GOLD }}>
                            <Icon name="PackagePlus" size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <span className="font-oranienbaum text-white/30 text-xs">
            {facturas?.length === 0 ? '0 – 0 de 0' : `${(page - 1) * PAGE_SIZE + 1} – ${Math.min(page * PAGE_SIZE, facturas?.length)} de ${facturas?.length}`} resultados
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name="ChevronLeft" size={14} />
            </button>
            <span className="px-3 py-1 rounded-lg font-oranienbaum text-xs" style={{ background: `${BLUE}15`, color: BLUE }}>{page}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name="ChevronRight" size={14} />
            </button>
          </div>
        </div>
      </div>
      {/* Manual Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.75)' }}>
          <div className="w-full max-w-4xl my-6 rounded-2xl overflow-hidden" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
              <h2 className="font-oranienbaum font-bold text-white text-lg">{editFactura ? `Editar Factura ${editFactura?.numero_factura}` : 'Nueva Factura Manual'}</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg text-white/40 hover:text-white/70 transition-colors"><Icon name="X" size={18} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-2">
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1.5">Proveedor *</label>
                  <select value={form?.proveedor_id} onChange={e => setForm(f => ({ ...f, proveedor_id: e?.target?.value }))}
                    className="w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 outline-none"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <option value="">— Seleccionar proveedor —</option>
                    {proveedores?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1.5">Nº Factura *</label>
                  <input type="text" value={form?.numero_factura} onChange={e => setForm(f => ({ ...f, numero_factura: e?.target?.value }))}
                    placeholder="FAC-2024-001"
                    className="w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1.5">Fecha *</label>
                  <input type="date" value={form?.fecha_factura} onChange={e => setForm(f => ({ ...f, fecha_factura: e?.target?.value }))}
                    className="w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 outline-none"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', colorScheme: 'dark' }} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-oranienbaum text-white/60 text-xs uppercase tracking-wider">Líneas de Factura</h3>
                  <button onClick={addLinea} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                    style={{ background: `${ACCENT}10`, color: ACCENT, border: `1px solid ${ACCENT}25` }}>
                    <Icon name="Plus" size={12} />Añadir línea
                  </button>
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="grid gap-2 px-3 py-2" style={{ gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr auto', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {['Producto', 'Descripción', 'Cantidad', 'P. Unitario', 'Subtotal', '']?.map(h => (
                      <span key={h} className="font-oranienbaum text-white/35 text-xs uppercase tracking-wider">{h}</span>
                    ))}
                  </div>
                  {lineas?.map(linea => (
                    <div key={linea?._key} className="grid gap-2 px-3 py-2 items-center"
                      style={{ gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr auto', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <select value={linea?.producto_id} onChange={e => updateLinea(linea?._key, 'producto_id', e?.target?.value)}
                        className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                        <option value="">— Producto —</option>
                        {productos?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}</option>)}
                      </select>
                      <input type="text" value={linea?.descripcion} onChange={e => updateLinea(linea?._key, 'descripcion', e?.target?.value)}
                        placeholder="Descripción"
                        className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }} />
                      <input type="number" min="0" step="0.001" value={linea?.cantidad} onChange={e => updateLinea(linea?._key, 'cantidad', e?.target?.value)}
                        placeholder="0" className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 placeholder-white/25 outline-none text-right"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }} />
                      <input type="number" min="0" step="0.01" value={linea?.precio_unitario} onChange={e => updateLinea(linea?._key, 'precio_unitario', e?.target?.value)}
                        placeholder="0.00" className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 placeholder-white/25 outline-none text-right"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }} />
                      <span className="font-oranienbaum text-white/70 text-xs text-right pr-1">{fmtEur(parseFloat(linea?.subtotal) || 0)}</span>
                      <button onClick={() => removeLinea(linea?._key)} disabled={lineas?.length === 1}
                        className="p-1 rounded-lg text-white/30 hover:text-red-400 transition-colors disabled:opacity-20">
                        <Icon name="Trash2" size={13} />
                      </button>
                    </div>
                  ))}
                  <div className="px-3 py-3 flex justify-end gap-6" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="text-right"><p className="font-oranienbaum text-white/40 text-xs">Base Imponible</p><p className="font-oranienbaum text-white/80 text-sm font-semibold">{fmtEur(baseImponible)}</p></div>
                    <div className="text-right"><p className="font-oranienbaum text-white/40 text-xs">IVA (est. 10%)</p><p className="font-oranienbaum text-white/80 text-sm font-semibold">{fmtEur(ivaEstimado)}</p></div>
                    <div className="text-right"><p className="font-oranienbaum text-white/40 text-xs">Total</p><p className="font-oranienbaum text-white font-bold text-lg">{fmtEur(totalEstimado)}</p></div>
                  </div>
                </div>
              </div>

              {formError && (
                <div className="p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                  <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
                  <p className="font-oranienbaum text-red-400 text-sm">{formError}</p>
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button onClick={() => setShowForm(false)} disabled={formSaving}
                  className="px-4 py-2 rounded-xl text-sm font-oranienbaum transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
                  Cancelar
                </button>
                <button onClick={saveFactura} disabled={formSaving}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-oranienbaum font-semibold transition-all"
                  style={{ background: formSaving ? 'rgba(96,165,250,0.10)' : `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
                  {formSaving ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#60A5FA] animate-spin" />Guardando...</> : <><Icon name="Save" size={14} />Guardar Factura</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Detail Modal */}
      {(detailFactura || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.75)' }}>
          <div className="w-full max-w-2xl my-6 rounded-2xl overflow-hidden" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
              <h2 className="font-oranienbaum font-bold text-white text-lg">Detalle de Factura</h2>
              <button onClick={() => setDetailFactura(null)} className="p-2 rounded-lg text-white/40 hover:text-white/70 transition-colors"><Icon name="X" size={18} /></button>
            </div>
            <div className="p-6">
              {detailLoading ? (
                <div className="flex justify-center py-8"><div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#60A5FA] animate-spin" /></div>
              ) : detailError ? (
                <p className="font-oranienbaum text-red-400 text-sm">{detailError}</p>
              ) : detailFactura && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField label="Número" value={detailFactura?.numero_factura} />
                    <InfoField label="Fecha" value={fmtDate(detailFactura?.fecha_factura)} />
                    <InfoField label="Proveedor" value={detailFactura?.proveedor?.nombre || '—'} />
                    <InfoField label="CIF Proveedor" value={detailFactura?.cif_proveedor || '—'} />
                    <InfoField label="Vencimiento" value={detailFactura?.fecha_vencimiento ? fmtDate(detailFactura?.fecha_vencimiento) : 'Sin definir'} />
                    <InfoField label="Forma de Pago" value={detailFactura?.forma_pago || '—'} />
                    <InfoField label="Base Imponible" value={fmtEur(detailFactura?.base_imponible)} />
                    <InfoField label="IVA" value={fmtEur(detailFactura?.iva)} />
                    <InfoField label="Total" value={fmtEur(detailFactura?.total)} highlight />
                    <InfoField label="Pendiente de Pago" value={fmtEur(parseFloat(detailFactura?.pendiente_pago ?? detailFactura?.total ?? 0))} />
                    <InfoField label="Estado" value={detailFactura?.estado || 'PENDIENTE'} />
                    <InfoField label="Estado de Pago" value={detailFactura?.estado_pago || 'PENDIENTE'} />
                    <InfoField label="Confianza OCR" value={
                      detailFactura?.ocr_procesado
                        ? `${detailFactura?.ocr_confianza != null
                            ? (detailFactura?.ocr_confianza > 1
                                ? Math.round(detailFactura?.ocr_confianza)
                                : Math.round(detailFactura?.ocr_confianza * 100))
                            : 'N/D'}%`
                        : 'Manual'
                    } />
                    <InfoField label="Origen" value={detailFactura?.origen || 'MANUAL'} />
                    {detailFactura?.filename_original && (
                      <InfoField label="Archivo original" value={detailFactura?.filename_original} />
                    )}
                  </div>
                  {/* Proveedor no identificado — mostrar cuando proveedor_id es null */}
                  {!detailFactura?.proveedor_id && (
                    <div className="p-4 rounded-xl" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)' }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2">
                          <Icon name="AlertTriangle" size={15} style={{ color: '#FBBF24', flexShrink: 0, marginTop: 1 }} />
                          <div>
                            <p className="font-oranienbaum text-amber-300 text-sm font-semibold">Proveedor no identificado</p>
                            <p className="font-oranienbaum text-white/40 text-xs mt-0.5">
                              El OCR no pudo asociar esta factura a ningún proveedor registrado.
                            </p>
                            {/* Show OCR-detected data if available */}
                            {(() => {
                              const ocrJson = detailFactura?.ocr_json || {};
                              const ocrEmisor = ocrJson?.emisor || ocrJson?.proveedor || {};
                              const ocrRazonSocial = detailFactura?.proveedor_detectado_nombre || ocrEmisor?.razon_social || null;
                              const ocrCif = detailFactura?.proveedor_detectado_nif_cif || ocrEmisor?.nif_cif || detailFactura?.cif_proveedor || null;
                              const ocrEmail = detailFactura?.proveedor_detectado_email || ocrEmisor?.email || null;
                              const ocrTelefono = detailFactura?.proveedor_detectado_telefono || ocrEmisor?.telefono || null;
                              const ocrDireccion = detailFactura?.proveedor_detectado_direccion || ocrEmisor?.direccion || null;
                              if (!ocrRazonSocial && !ocrCif && !ocrEmail) return null;
                              return (
                                <div className="mt-2 space-y-0.5">
                                  <p className="font-oranienbaum text-white/35 text-[10px] uppercase tracking-wider mb-1">Datos detectados por OCR:</p>
                                  {ocrRazonSocial && <p className="font-oranienbaum text-white/60 text-xs">· Razón social: <span className="text-white/80 font-semibold">{ocrRazonSocial}</span></p>}
                                  {ocrCif && <p className="font-oranienbaum text-white/60 text-xs">· CIF/NIF: <span className="text-white/80 font-mono">{ocrCif}</span></p>}
                                  {ocrEmail && <p className="font-oranienbaum text-white/60 text-xs">· Email: <span className="text-white/80">{ocrEmail}</span></p>}
                                  {ocrTelefono && <p className="font-oranienbaum text-white/60 text-xs">· Teléfono: <span className="text-white/80">{ocrTelefono}</span></p>}
                                  {ocrDireccion && <p className="font-oranienbaum text-white/60 text-xs">· Dirección: <span className="text-white/80">{ocrDireccion}</span></p>}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <button
                          onClick={() => setAsignarProveedorFactura(detailFactura)}
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum font-semibold transition-all"
                          style={{ background: 'rgba(251,191,36,0.18)', border: '1px solid rgba(251,191,36,0.40)', color: '#FBBF24' }}
                        >
                          <Icon name="Truck" size={12} />Asignar proveedor
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Proveedor identificado — mostrar nombre + badge si fue auto-creado */}
                  {detailFactura?.proveedor_id && (
                    <div className="p-3 rounded-xl" style={{ background: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.18)' }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2">
                          <Icon name="Truck" size={14} style={{ color: '#34D399', flexShrink: 0, marginTop: 1 }} />
                          <div>
                            <p className="font-oranienbaum text-white/80 text-sm font-semibold">
                              {detailFactura?.proveedor?.nombre || detailFactura?.proveedor_detectado_nombre || '—'}
                            </p>
                            {detailFactura?.cif_proveedor && (
                              <p className="font-oranienbaum text-white/40 text-xs font-mono mt-0.5">{detailFactura?.cif_proveedor}</p>
                            )}
                            {detailFactura?.proveedor_auto_creado && (
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
                                  style={{ background: 'rgba(96,165,250,0.12)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.25)' }}>
                                  <Icon name="Wand2" size={9} />Proveedor creado automáticamente desde OCR
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <a
                          href="/tienda-proveedores"
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-oranienbaum transition-all"
                          style={{ background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.25)', color: '#34D399' }}
                        >
                          <Icon name="ExternalLink" size={11} />Ver proveedor
                        </a>
                      </div>
                      {/* OCR traceability: show what OCR originally detected */}
                      {(detailFactura?.proveedor_detectado_nombre || detailFactura?.proveedor_detectado_nif_cif) && (
                        <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <p className="font-oranienbaum text-white/25 text-[10px] uppercase tracking-wider mb-1">Detectado originalmente por OCR:</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                            {detailFactura?.proveedor_detectado_nombre && (
                              <span className="font-oranienbaum text-white/35 text-[10px]">Nombre: {detailFactura?.proveedor_detectado_nombre}</span>
                            )}
                            {detailFactura?.proveedor_detectado_nif_cif && (
                              <span className="font-oranienbaum text-white/35 text-[10px] font-mono">CIF: {detailFactura?.proveedor_detectado_nif_cif}</span>
                            )}
                            {detailFactura?.proveedor_detectado_email && (
                              <span className="font-oranienbaum text-white/35 text-[10px]">Email: {detailFactura?.proveedor_detectado_email}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Document origin info for email invoices */}
                  {detailFactura?.origen === 'EMAIL' && (
                    <div className="p-3 rounded-xl" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.15)' }}>
                      <p className="font-oranienbaum text-purple-300/70 text-[10px] uppercase tracking-wider mb-2">Origen: Email</p>
                      <div className="grid grid-cols-2 gap-2 text-xs font-oranienbaum text-white/50">
                        {detailFactura?.gmail_message_id && <span>Message ID: <span className="text-white/30 text-[10px] break-all">{detailFactura?.gmail_message_id?.slice(0, 16)}…</span></span>}
                        {detailFactura?.filename_original && <span>Archivo: <span className="text-white/70">{detailFactura?.filename_original}</span></span>}
                      </div>
                    </div>
                  )}
                  {detailFactura?.lineas?.length > 0 && (
                    <div>
                      <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider mb-2">Líneas</p>
                      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
                        <table className="w-full">
                          <thead>
                            <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                              {['Producto', 'Descripción', 'Cant.', 'P. Unit.', 'Subtotal']?.map(h => (
                                <th key={h} className="px-3 py-2 text-left"><span className="font-oranienbaum text-white/35 text-xs">{h}</span></th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {detailFactura?.lineas?.map(l => (
                              <tr key={l?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                <td className="px-3 py-2"><span className="font-oranienbaum text-white/70 text-xs">{l?.producto?.nombre || '—'}</span></td>
                                <td className="px-3 py-2"><span className="font-oranienbaum text-white/55 text-xs">{l?.descripcion_original || l?.descripcion || '—'}</span></td>
                                <td className="px-3 py-2 text-right"><span className="font-oranienbaum text-white/70 text-xs">{l?.cantidad}</span></td>
                                <td className="px-3 py-2 text-right"><span className="font-oranienbaum text-white/70 text-xs">{fmtEur(l?.precio_unitario)}</span></td>
                                <td className="px-3 py-2 text-right"><span className="font-oranienbaum text-white/80 text-xs font-semibold">{fmtEur(l?.subtotal)}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {/* Ver / Descargar documento original */}
                    {(detailFactura?.storage_bucket && detailFactura?.storage_path) ? (
                      <>
                        <VerDocumentoOriginal factura={detailFactura} mode="view" />
                        <VerDocumentoOriginal factura={detailFactura} mode="download" />
                      </>
                    ) : (
                      <span className="font-oranienbaum text-white/30 text-[10px] italic self-center">
                        Documento original no disponible
                      </span>
                    )}
                    <button onClick={() => { setDetailFactura(null); setVencimientoFactura(detailFactura); }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
                      style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
                      <Icon name="Calendar" size={12} />Editar vencimiento
                    </button>
                    {detailFactura?.estado_pago !== 'PAGADA' && detailFactura?.estado_pago !== 'ANULADA' && (
                      <button onClick={() => { setDetailFactura(null); setPagoFactura(detailFactura); }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
                        style={{ background: `${ACCENT}15`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>
                        <Icon name="CreditCard" size={12} />Registrar pago
                      </button>
                    )}
                    {detailFactura?.ocr_procesado && (
                      <button onClick={() => { setDetailFactura(null); setCompletarFactura(detailFactura); }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
                        style={{ background: `${GOLD}15`, color: GOLD, border: `1px solid ${GOLD}30` }}>
                        <Icon name="PackagePlus" size={12} />Completar importación
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Recepcionar Modal */}
      {recepcionFactura && (        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.80)' }}>
          <div className="w-full max-w-3xl my-6 rounded-2xl overflow-hidden" style={{ background: '#111111', border: `1px solid ${ACCENT}30` }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${ACCENT}20`, background: `${ACCENT}08` }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
                  <Icon name="PackageCheck" size={16} style={{ color: ACCENT }} />
                </div>
                <div>
                  <h2 className="font-oranienbaum font-bold text-white text-lg">Recepcionar Mercancía</h2>
                  <p className="font-oranienbaum text-white/40 text-xs">Factura {recepcionFactura?.numero_factura} · {recepcionFactura?.proveedor?.nombre}</p>
                </div>
              </div>
              <button onClick={() => { setRecepcionFactura(null); setRecepcionSuccess(null); }} className="p-2 rounded-lg text-white/40 hover:text-white/70 transition-colors"><Icon name="X" size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              {recepcionSuccess ? (
                <div className="flex flex-col items-center gap-4 py-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
                    <Icon name="CheckCircle2" size={32} style={{ color: ACCENT }} />
                  </div>
                  <p className="font-oranienbaum text-white font-semibold text-center">{recepcionSuccess}</p>
                  <button onClick={() => { setRecepcionFactura(null); setRecepcionSuccess(null); }}
                    className="px-5 py-2 rounded-xl text-sm font-oranienbaum font-semibold"
                    style={{ background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` }}>Cerrar</button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block font-oranienbaum text-white/50 text-xs mb-1.5">Almacén de Destino *</label>
                    <select value={recepcionAlmacen} onChange={e => setRecepcionAlmacen(e?.target?.value)}
                      className="w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 outline-none"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                      <option value="">— Seleccionar almacén —</option>
                      {almacenes?.map(a => <option key={a?.id} value={a?.id}>{a?.nombre}</option>)}
                    </select>
                  </div>
                  {recepcionError && (
                    <div className="p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                      <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
                      <p className="font-oranienbaum text-red-400 text-sm">{recepcionError}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button onClick={() => setRecepcionFactura(null)} disabled={recepcionLoading}
                      className="px-4 py-2 rounded-xl text-sm font-oranienbaum transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
                      Cancelar
                    </button>
                    <button onClick={ejecutarRecepcion} disabled={recepcionLoading}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-oranienbaum font-bold transition-all"
                      style={{ background: recepcionLoading ? `${ACCENT}10` : `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
                      {recepcionLoading ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#34D399] animate-spin" />Procesando...</> : <><Icon name="PackageCheck" size={15} />RECEPCIONAR MERCANCÍA</>}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </> /* end facturas tab */}
    </div>
  );
};

const InfoField = ({ label, value, highlight = false }) => (
  <div>
    <p className="font-oranienbaum text-white/35 text-xs mb-0.5">{label}</p>
    <p className={`font-oranienbaum text-sm ${highlight ? 'text-white font-bold text-base' : 'text-white/75'}`}>{value}</p>
  </div>
);

export default TiendaCompras;
